// ── SubAgents.js — Sub-Agent Partner Management Module ───────────────────────
// Module-gated: company.modules.subAgents must be true (toggled by super admin).
// Completely separate data + team from main CRM leads — sub-agent students/
// applications never appear in the main Leads / Pipeline / Applications pages.
//
// Collections under /companies/{companyId}/:
//   subAgentOrgs          – partner sub-agent organizations
//   subAgentStudents      – students submitted via sub-agents (NOT main leads)
//   subAgentUniversities  – universities + embedded commission entries
//
// Tabs: Overview | Organizations | Universities | Applications | Commission

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { db } from '../firebase/config';
import {
  collection, query, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, orderBy,
} from 'firebase/firestore';
import { getStorage, ref as sRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Constants ──────────────────────────────────────────────────────────────────

const APP_STAGES = [
  { id: 'draft',               label: 'Draft',               color: '#94a3b8', bg: '#f1f5f9' },
  { id: 'submitted',           label: 'Submitted',           color: '#f59e0b', bg: '#fffbeb' },
  { id: 'under_review',        label: 'Under Review',        color: '#3b82f6', bg: '#eff6ff' },
  { id: 'conditional_offer',   label: 'Conditional Offer',   color: '#f97316', bg: '#fff7ed' },
  { id: 'unconditional_offer', label: 'Unconditional Offer', color: '#10b981', bg: '#ecfdf5' },
  { id: 'visa_applied',        label: 'Visa Applied',        color: '#8b5cf6', bg: '#f5f3ff' },
  { id: 'visa_approved',       label: 'Visa Approved ✓',     color: '#059669', bg: '#d1fae5' },
  { id: 'visa_rejected',       label: 'Visa Rejected',       color: '#ef4444', bg: '#fef2f2' },
  { id: 'enrolled',            label: 'Enrolled 🎓',         color: '#7c3aed', bg: '#ede9fe' },
  { id: 'deferred',            label: 'Deferred',            color: '#eab308', bg: '#fefce8' },
  { id: 'withdrawn',           label: 'Withdrawn',           color: '#6b7280', bg: '#f9fafb' },
];

const DEGREE_TYPES   = ['Bachelors', 'Bachelors Transfer', 'Masters', 'PhD'];
const COMMISSION_TYPES = ['Fixed', 'Percentage', 'Custom', 'Scholarship'];
const DURATION_TYPES = ['Semester-Wise', 'Year-Wise'];
const STEM_TYPES = ['STEM', 'Non-STEM', 'Business', 'Arts & Humanities', 'Social Sciences', 'Law', 'Medicine', 'Education', 'Other'];
const CURRENCIES = ['USD', 'GBP', 'CAD', 'AUD', 'EUR', 'INR', 'AED', 'SGD', 'NZD'];
const INTAKE_OPTIONS = [
  'Fall 2024', 'Spring 2025', 'Summer 2025', 'Fall 2025',
  'Spring 2026', 'Summer 2026', 'Fall 2026', 'Spring 2027',
];
const CONTRACT_TYPES = ['Direct', 'Shorelight', 'Other'];
const COUNTRIES = [
  'India', 'UAE', 'Pakistan', 'Bangladesh', 'Nepal', 'Sri Lanka', 'Nigeria',
  'Kenya', 'Ghana', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
  'USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Netherlands',
  'Singapore', 'Malaysia', 'Philippines', 'Vietnam', 'Indonesia', 'Other',
];
const UNI_COUNTRIES = [
  'USA', 'UK', 'Canada', 'Australia', 'Germany', 'Netherlands', 'France',
  'Ireland', 'New Zealand', 'Singapore', 'Switzerland', 'Sweden', 'Other',
];

// ── Shared Style Helpers ───────────────────────────────────────────────────────

const card = {
  background: 'var(--bg-primary)',
  borderRadius: 14,
  border: '1px solid var(--border-subtle)',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

function btn(variant = 'primary', size = 'md') {
  const base = {
    border: 'none', borderRadius: 9, cursor: 'pointer', fontWeight: 600,
    fontFamily: "'Inter', sans-serif", transition: 'all 0.15s',
    padding: size === 'sm' ? '5px 12px' : '9px 20px',
    fontSize: size === 'sm' ? 12 : 13,
  };
  if (variant === 'primary') return { ...base, background: 'var(--color-primary)', color: '#fff' };
  if (variant === 'ghost')   return { ...base, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' };
  if (variant === 'danger')  return { ...base, background: '#fee2e2', color: '#dc2626', border: 'none' };
  if (variant === 'green')   return { ...base, background: '#ecfdf5', color: '#059669', border: 'none' };
  return base;
}

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 9,
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-primary)', color: 'var(--text-primary)',
  fontSize: 13, fontFamily: "'Inter', sans-serif",
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  marginBottom: 4, display: 'block',
};

// ── Micro-components ──────────────────────────────────────────────────────────

function StagePill({ stageId, onChange }) {
  const [open, setOpen] = useState(false);
  const s = APP_STAGES.find(x => x.id === stageId) || APP_STAGES[0];
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onClick={() => onChange && setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          color: s.color, background: s.bg, cursor: onChange ? 'pointer' : 'default',
          border: `1px solid ${s.color}33`, whiteSpace: 'nowrap',
        }}
      >
        {s.label}{onChange ? ' ▾' : ''}
      </span>
      {open && onChange && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, zIndex: 999,
          background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          minWidth: 190, overflow: 'hidden',
        }}>
          {APP_STAGES.map(st => (
            <div
              key={st.id}
              onClick={() => { onChange(st.id); setOpen(false); }}
              style={{
                padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                color: st.color, background: stageId === st.id ? st.bg : 'transparent',
              }}
            >{st.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, width = 580 }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: '100%', maxWidth: width, maxHeight: '92vh',
        overflowY: 'auto', padding: 28, position: 'relative',
        background: 'var(--bg-primary)',
        borderRadius: 16,
        border: '1px solid var(--border-subtle)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          <button onClick={onClose} style={{ ...btn('ghost', 'sm'), padding: '4px 10px', fontSize: 15, lineHeight: 1 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label: lbl, children, half }) {
  return (
    <div style={{ flex: half ? '0 0 calc(50% - 8px)' : '1 1 100%', minWidth: 0 }}>
      <label style={labelStyle}>{lbl}</label>
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: 1,
      borderTop: '1px solid var(--border-subtle)', paddingTop: 16, marginTop: 4,
    }}>{children}</div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════════

function OverviewTab({ orgs, students, universities }) {
  const totalOrgs     = orgs.length;
  const totalStudents = students.length;
  const visaApproved  = students.filter(s => s.stage === 'visa_approved').length;
  const enrolled      = students.filter(s => s.stage === 'enrolled').length;
  const inProgress    = students.filter(s => ['submitted','under_review','conditional_offer','unconditional_offer','visa_applied'].includes(s.stage)).length;
  const visaRejected  = students.filter(s => s.stage === 'visa_rejected').length;
  const deferred      = students.filter(s => s.stage === 'deferred').length;
  const withdrawn     = students.filter(s => s.stage === 'withdrawn').length;
  const conversionPct = totalStudents > 0 ? Math.round((enrolled / totalStudents) * 100) : 0;

  const stageCounts = APP_STAGES.map(st => ({ ...st, count: students.filter(s => (s.stage || 'draft') === st.id).length }));
  const maxStageCount = Math.max(...stageCounts.map(s => s.count), 1);

  const topOrgs = useMemo(() => {
    const counts = {};
    students.forEach(s => { counts[s.subAgentOrgId] = (counts[s.subAgentOrgId] || 0) + 1; });
    return orgs.map(o => ({ ...o, appCount: counts[o.id] || 0 })).sort((a, b) => b.appCount - a.appCount).slice(0, 6);
  }, [orgs, students]);

  const recentStudents = useMemo(() =>
    [...students].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).slice(0, 8),
    [students]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>

      {/* ── PREMIUM HERO BANNER ─────────────────────────────────────────── */}
      <div style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #312e81 70%, #4c1d95 100%)', padding: '36px 36px 32px' }}>
        {/* Grid overlay */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.05 }} preserveAspectRatio="none">
          <defs><pattern id="sa-grid" width="44" height="44" patternUnits="userSpaceOnUse"><path d="M 44 0 L 0 0 0 44" fill="none" stroke="white" strokeWidth="1"/></pattern></defs>
          <rect width="100%" height="100%" fill="url(#sa-grid)" />
        </svg>
        {/* Blur orbs */}
        <div style={{ position: 'absolute', top: -80, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'rgba(139,92,246,0.22)', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, left: '20%', width: 250, height: 250, borderRadius: '50%', background: 'rgba(59,130,246,0.18)', filter: 'blur(45px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '30%', right: '35%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(236,72,153,0.10)', filter: 'blur(35px)', pointerEvents: 'none' }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: 'linear-gradient(135deg,rgba(139,92,246,0.7),rgba(59,130,246,0.7))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px rgba(139,92,246,0.4)' }}>🤝</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: -0.5, lineHeight: 1.2 }}>Sub-Agent Partner Network</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>Centralized partner management · Applications · Commission tracking</div>
            </div>
          </div>

          {/* Big 4 hero stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Partner Organizations', value: totalOrgs, suffix: '', icon: '🏢', accent: '#a78bfa' },
              { label: 'Student Applications', value: totalStudents, suffix: '', icon: '🎓', accent: '#60a5fa' },
              { label: 'University Partners', value: universities.length, suffix: '', icon: '🏛️', accent: '#34d399' },
              { label: 'Enrollment Rate', value: conversionPct, suffix: '%', icon: '📈', accent: '#fbbf24' },
            ].map(stat => (
              <div key={stat.label} style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)', borderRadius: 18, padding: '22px 24px', border: '1px solid rgba(255,255,255,0.13)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -12, right: -12, fontSize: 52, opacity: 0.08, lineHeight: 1 }}>{stat.icon}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.icon} {stat.label}</div>
                <div style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
                  {stat.value}<span style={{ fontSize: 22, fontWeight: 700, color: stat.accent }}>{stat.suffix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── QUICK METRIC STRIP ───────────────────────────────────────────── */}
      <div style={{ padding: '20px 28px 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px,1fr))', gap: 12 }}>
        {[
          { label: 'Visa Approved', value: visaApproved, color: '#059669', bg: '#d1fae5', icon: '✅' },
          { label: 'Enrolled', value: enrolled, color: '#7c3aed', bg: '#ede9fe', icon: '🎓' },
          { label: 'In Progress', value: inProgress, color: '#3b82f6', bg: '#eff6ff', icon: '⚡' },
          { label: 'Visa Rejected', value: visaRejected, color: '#ef4444', bg: '#fef2f2', icon: '❌' },
          { label: 'Deferred', value: deferred, color: '#eab308', bg: '#fefce8', icon: '⏸' },
          { label: 'Withdrawn', value: withdrawn, color: '#94a3b8', bg: '#f1f5f9', icon: '↩' },
        ].map(k => (
          <div key={k.label} style={{ ...card, padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{k.icon} {k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── ANALYSIS PANELS ──────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 28px 0' }}>

        {/* Pipeline Breakdown */}
        <div style={{ ...card, padding: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📊</span>
            Applications by Stage
          </div>
          {stageCounts.filter(s => s.count > 0).length === 0
            ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>No applications yet</div>
            : stageCounts.filter(s => s.count > 0).map(s => (
              <div key={s.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: s.color }}>{s.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{s.count}</span>
                </div>
                <div style={{ height: 7, borderRadius: 4, background: 'var(--bg-secondary)' }}>
                  <div style={{ height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${s.color}cc, ${s.color})`, width: `${(s.count / maxStageCount) * 100}%`, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
        </div>

        {/* Top Partner Orgs */}
        <div style={{ ...card, padding: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏆</span>
            Top Partner Organizations
          </div>
          {topOrgs.length === 0
            ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>No organizations yet</div>
            : topOrgs.map((o, i) => {
              const colors = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'];
              const bgs    = ['#ede9fe','#eff6ff','#ecfdf5','#fffbeb','#fef2f2','#f5f3ff'];
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 9, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: bgs[i] || '#f1f5f9', color: colors[i] || '#64748b', border: `1px solid ${colors[i] || '#e2e8f0'}33` }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{[o.city, o.country].filter(Boolean).join(', ') || '—'}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: bgs[i] || '#f1f5f9', color: colors[i] || '#64748b', flexShrink: 0 }}>{o.appCount} apps</span>
                </div>
              );
            })}
        </div>
      </div>

      {/* ── RECENT APPLICATIONS ──────────────────────────────────────────── */}
      {recentStudents.length > 0 && (
        <div style={{ ...card, margin: '20px 28px 28px', padding: 22, overflow: 'auto' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📋</span>
            Recent Applications
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-subtle)' }}>
                {['Student', 'Organization', 'University', 'Program', 'Intake', 'Stage'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentStudents.map(s => {
                const org = orgs.find(o => o.id === s.subAgentOrgId);
                const uni = universities.find(u => u.id === s.universityId);
                const st  = APP_STAGES.find(x => x.id === (s.stage || 'draft'));
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '11px 12px', fontWeight: 600, color: 'var(--text-primary)' }}>{s.studentName}</td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-secondary)' }}>{org?.name || '—'}</td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-secondary)' }}>{uni?.name || '—'}</td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.program || '—'}</td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-secondary)' }}>{s.intake || '—'}</td>
                    <td style={{ padding: '11px 12px' }}>
                      {st && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: st.color, background: st.bg }}>{st.label}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalStudents === 0 && totalOrgs === 0 && (
        <div style={{ margin: '24px 28px 28px', ...card, padding: '52px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🌐</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Build Your Sub-Agent Network</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 440, margin: '0 auto', lineHeight: 1.8 }}>
            Start by adding partner organizations and universities, then track student applications and commissions — all separate from your main CRM leads.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
            {[{ icon: '🏢', label: 'Add Organizations', tab: 'Organizations' }, { icon: '🏛️', label: 'Add Universities', tab: 'Universities' }].map(a => (
              <div key={a.label} style={{ padding: '10px 20px', borderRadius: 12, background: 'var(--color-primary-soft)', color: 'var(--color-primary)', fontWeight: 700, fontSize: 13, border: '1px solid var(--color-primary-light)' }}>{a.icon} {a.label}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FileUploadField ───────────────────────────────────────────────────────────
function FileUploadField({ label, storedUrl, storedName, onUploadComplete, uploadPath }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');
  const inputRef = useRef();
  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    let st; try { st = getStorage(); } catch (_) { st = null; }
    if (!st) { onUploadComplete({ url: '', name: file.name }); return; }
    setUploading(true); setErr('');
    const fileRef = sRef(st, `${uploadPath}/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(fileRef, file);
    task.on('state_changed',
      snap => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      e2 => { setErr(e2.message); setUploading(false); },
      async () => { const url = await getDownloadURL(task.snapshot.ref); onUploadComplete({ url, name: file.name }); setUploading(false); setProgress(0); }
    );
  };
  return (
    <div>
      {label && <label style={labelStyle}>{label}</label>}
      {storedUrl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border-subtle)', background: 'var(--bg-secondary)' }}>
          <span style={{ fontSize: 18 }}>📎</span>
          <a href={storedUrl} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 13, color: 'var(--color-primary)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{storedName || 'View File'}</a>
          <button style={btn('ghost', 'sm')} onClick={() => { if (inputRef.current) inputRef.current.value = ''; onUploadComplete({ url: '', name: '' }); }}>Remove</button>
          <button style={btn('ghost', 'sm')} onClick={() => inputRef.current && inputRef.current.click()}>Replace</button>
        </div>
      ) : (
        <div style={{ border: '2px dashed var(--border-subtle)', borderRadius: 9, padding: 20, textAlign: 'center', cursor: 'pointer' }} onClick={() => inputRef.current && inputRef.current.click()}>
          {uploading ? (
            <><div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Uploading… {progress}%</div>
            <div style={{ height: 4, background: 'var(--bg-secondary)', borderRadius: 2 }}><div style={{ height: '100%', background: 'var(--color-primary)', borderRadius: 2, width: `${progress}%`, transition: 'width 0.2s' }} /></div></>
          ) : (
            <><div style={{ fontSize: 24, marginBottom: 6 }}>📄</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Click to upload (PDF, DOCX, JPG)</div></>
          )}
        </div>
      )}
      {err && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{err}</div>}
      <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleFile} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
    </div>
  );
}

// ── OwnersEditor ──────────────────────────────────────────────────────────────
function OwnersEditor({ owners = [], onChange }) {
  const add = () => onChange([...owners, { name: '', phone: '', email: '' }]);
  const upd = (i, k, v) => { const a = [...owners]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const rem = i => onChange(owners.filter((_, idx) => idx !== i));
  return (
    <div>
      {owners.map((o, i) => (
        <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Owner / Director #{i + 1}</span>
            {owners.length > 1 && <button style={btn('danger', 'sm')} onClick={() => rem(i)}>Remove</button>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ flex: '1 1 100%' }}><label style={labelStyle}>Full Name *</label>
              <input style={inputStyle} value={o.name} onChange={e => upd(i, 'name', e.target.value)} placeholder="Full name" /></div>
            <div style={{ flex: '1 1 calc(50% - 5px)', minWidth: 130 }}><label style={labelStyle}>Phone</label>
              <input style={inputStyle} value={o.phone} onChange={e => upd(i, 'phone', e.target.value)} placeholder="+91 XXXXX XXXXX" /></div>
            <div style={{ flex: '1 1 calc(50% - 5px)', minWidth: 130 }}><label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={o.email} onChange={e => upd(i, 'email', e.target.value)} placeholder="owner@company.com" /></div>
          </div>
        </div>
      ))}
      <button style={{ ...btn('ghost', 'sm'), marginTop: 2 }} onClick={add}>+ Add {owners.length === 0 ? 'Owner' : 'Another Owner'}</button>
    </div>
  );
}

// ── OrgUniAssignment ──────────────────────────────────────────────────────────
function OrgUniAssignment({ assigned = [], universities, onChange }) {
  const ids = assigned.map(a => a.uniId);
  const allSel = universities.length > 0 && universities.every(u => ids.includes(u.id));
  const toggleAll = () => allSel
    ? onChange([])
    : onChange(universities.map(u => ({ uniId: u.id, commissionSharePct: assigned.find(a => a.uniId === u.id)?.commissionSharePct || '' })));
  const toggle = uid => ids.includes(uid)
    ? onChange(assigned.filter(a => a.uniId !== uid))
    : onChange([...assigned, { uniId: uid, commissionSharePct: '' }]);
  const updPct = (uid, pct) => onChange(assigned.map(a => a.uniId === uid ? { ...a, commissionSharePct: pct } : a));

  if (!universities.length) return <div style={{ padding: '10px 0', color: 'var(--text-muted)', fontSize: 13 }}>No universities added yet. Add universities in the Universities tab first.</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 9 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>
          <input type="checkbox" checked={allSel} onChange={toggleAll} />
          Select All Universities
        </label>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{assigned.length} / {universities.length} selected</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
        {universities.map(u => {
          const isSel = ids.includes(u.id);
          const entry = assigned.find(a => a.uniId === u.id);
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 9, border: `1px solid ${isSel ? 'var(--color-primary)' : 'var(--border-subtle)'}`, background: isSel ? 'var(--color-primary-soft)' : 'var(--bg-secondary)', transition: 'all 0.15s' }}>
              <input type="checkbox" checked={isSel} onChange={() => toggle(u.id)} style={{ cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{u.country}{u.contractType ? ` · ${u.contractType}` : ''}</div>
              </div>
              {isSel && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Their share</span>
                  <div style={{ position: 'relative', width: 90 }}>
                    <input style={{ ...inputStyle, padding: '5px 24px 5px 9px', fontSize: 12, width: '100%' }} type="number" min="0" max="100" step="0.5" value={entry?.commissionSharePct || ''} onChange={e => updPct(u.id, e.target.value)} placeholder="0" />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--text-secondary)', pointerEvents: 'none' }}>%</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCsv(text) {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
  return lines.slice(1).filter(l => l.trim()).map(line => {
    const vals = []; let cur = '', inQ = false;
    for (const c of line + ',') {
      if (c === '"') { inQ = !inQ; continue; }
      if (c === ',' && !inQ) { vals.push(cur.trim()); cur = ''; continue; }
      cur += c;
    }
    const row = {}; headers.forEach((h, i) => { row[h] = vals[i] || ''; }); return row;
  });
}

// ── OrgBulkUpload ─────────────────────────────────────────────────────────────
function OrgBulkUpload({ companyId, onClose }) {
  const [step, setStep] = useState('upload');
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const hdrs = ['Organization Name','Country','State','City','Address Line 1','Pincode','Owner Name','Owner Email','Owner Phone','GST Number','Notes'];
  const dlTmpl = () => {
    const csv = hdrs.join(',') + '\n"Global Study Abroad Pvt. Ltd.","India","Maharashtra","Mumbai","123 MG Road","400001","Rahul Sharma","rahul@gsa.com","+91 9876543210","",""\n';
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='org_template.csv'; a.click();
  };
  const handleFile = e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = ev => { setRows(parseCsv(ev.target.result).filter(r => r['Organization Name'])); setStep('preview'); }; r.readAsText(f); };
  const doImport = async () => {
    setImporting(true);
    await Promise.all(rows.map(r => addDoc(collection(db,'companies',companyId,'subAgentOrgs'), {
      name: r['Organization Name'], country: r['Country']||'', state: r['State']||'', city: r['City']||'',
      addressLine1: r['Address Line 1']||'', addressLine2:'', pincode: r['Pincode']||'',
      owners: r['Owner Name'] ? [{name:r['Owner Name'],email:r['Owner Email']||'',phone:r['Owner Phone']||''}] : [],
      gstRegistered: !!r['GST Number'], gstNumber: r['GST Number']||'', notes: r['Notes']||'',
      status:'active', assignedUniversities:[], contractFileUrl:'', contractFileName:'', contractStart:'', contractRenewal:'',
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    })));
    setImporting(false); setStep('done');
  };
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {step==='upload' && (<>
        <div style={{ background:'#eff6ff', borderRadius:10, padding:13, fontSize:13, color:'#1e40af', lineHeight:1.6 }}>💡 Download the template CSV, fill in org details, then upload. Universities & commission shares can be added later from each organization's edit page.</div>
        <button style={btn('ghost')} onClick={dlTmpl}>⬇ Download Template CSV</button>
        <div style={{ border:'2px dashed var(--border-subtle)', borderRadius:10, padding:32, textAlign:'center', cursor:'pointer' }} onClick={() => document.getElementById('ob-inp').click()}>
          <div style={{ fontSize:28, marginBottom:8 }}>📂</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)' }}>Click to select CSV file</div>
          <input id="ob-inp" type="file" accept=".csv" style={{ display:'none' }} onChange={handleFile} />
        </div>
      </>)}
      {step==='preview' && (<>
        <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:14 }}>{rows.length} organizations ready to import</div>
        <div style={{ maxHeight:280, overflowY:'auto', border:'1px solid var(--border-subtle)', borderRadius:10 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr style={{ background:'var(--bg-secondary)' }}>{['Name','Country','City','Owner'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'var(--text-secondary)' }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((r,i)=><tr key={i} style={{ borderTop:'1px solid var(--border-subtle)' }}><td style={{ padding:'8px 12px' }}>{r['Organization Name']}</td><td style={{ padding:'8px 12px' }}>{r['Country']}</td><td style={{ padding:'8px 12px' }}>{r['City']}</td><td style={{ padding:'8px 12px' }}>{r['Owner Name']}</td></tr>)}</tbody>
          </table>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
          <button style={btn('ghost')} onClick={() => setStep('upload')}>← Back</button>
          <button style={btn('primary')} onClick={doImport} disabled={importing}>{importing ? 'Importing…' : `Import ${rows.length} Organizations`}</button>
        </div>
      </>)}
      {step==='done' && (
        <div style={{ textAlign:'center', padding:'24px 0' }}>
          <div style={{ fontSize:44, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)', marginBottom:6 }}>Import Complete!</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>{rows.length} organizations added.</div>
          <button style={btn('primary')} onClick={onClose}>Done</button>
        </div>
      )}
    </div>
  );
}

// ── UniBulkUpload ─────────────────────────────────────────────────────────────
function UniBulkUpload({ companyId, onClose }) {
  const [step, setStep] = useState('upload');
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const hdrs = ['University Name','Country','Contract Type','POC Name','POC Email','POC Phone','Notes'];
  const dlTmpl = () => {
    const csv = hdrs.join(',') + '\n"Adelphi University","USA","Direct","Jane Smith","jsmith@adelphi.edu","+1 555 123 4567",""\n';
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='university_template.csv'; a.click();
  };
  const handleFile = e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = ev => { setRows(parseCsv(ev.target.result).filter(r => r['University Name'])); setStep('preview'); }; r.readAsText(f); };
  const doImport = async () => {
    setImporting(true);
    await Promise.all(rows.map(r => addDoc(collection(db,'companies',companyId,'subAgentUniversities'), {
      name: r['University Name'], country: r['Country']||'', contractType: r['Contract Type']||'Direct',
      pocName: r['POC Name']||'', pocEmail: r['POC Email']||'', pocPhone: r['POC Phone']||'',
      notes: r['Notes']||'', promotedDegrees:[], commissions:[], programs:[],
      contractFileUrl:'', contractFileName:'', contractStart:'', contractRenewal:'', processDocUrl:'',
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    })));
    setImporting(false); setStep('done');
  };
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {step==='upload' && (<>
        <div style={{ background:'#eff6ff', borderRadius:10, padding:13, fontSize:13, color:'#1e40af', lineHeight:1.6 }}>💡 Download the template CSV, fill in university details, then upload. Programs, fee structure, and commission entries can be added later.</div>
        <button style={btn('ghost')} onClick={dlTmpl}>⬇ Download Template CSV</button>
        <div style={{ border:'2px dashed var(--border-subtle)', borderRadius:10, padding:32, textAlign:'center', cursor:'pointer' }} onClick={() => document.getElementById('ub-inp').click()}>
          <div style={{ fontSize:28, marginBottom:8 }}>📂</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)' }}>Click to select CSV file</div>
          <input id="ub-inp" type="file" accept=".csv" style={{ display:'none' }} onChange={handleFile} />
        </div>
      </>)}
      {step==='preview' && (<>
        <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:14 }}>{rows.length} universities ready to import</div>
        <div style={{ maxHeight:280, overflowY:'auto', border:'1px solid var(--border-subtle)', borderRadius:10 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr style={{ background:'var(--bg-secondary)' }}>{['Name','Country','Contract Type','POC'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'var(--text-secondary)' }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((r,i)=><tr key={i} style={{ borderTop:'1px solid var(--border-subtle)' }}><td style={{ padding:'8px 12px' }}>{r['University Name']}</td><td style={{ padding:'8px 12px' }}>{r['Country']}</td><td style={{ padding:'8px 12px' }}>{r['Contract Type']||'Direct'}</td><td style={{ padding:'8px 12px' }}>{r['POC Name']}</td></tr>)}</tbody>
          </table>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
          <button style={btn('ghost')} onClick={() => setStep('upload')}>← Back</button>
          <button style={btn('primary')} onClick={doImport} disabled={importing}>{importing ? 'Importing…' : `Import ${rows.length} Universities`}</button>
        </div>
      </>)}
      {step==='done' && (
        <div style={{ textAlign:'center', padding:'24px 0' }}>
          <div style={{ fontSize:44, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)', marginBottom:6 }}>Import Complete!</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>{rows.length} universities added.</div>
          <button style={btn('primary')} onClick={onClose}>Done</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — ORGANIZATIONS
// ═══════════════════════════════════════════════════════════════════════════════

function OrgForm({ initial, onSave, onClose, universities = [], companyId }) {
  const initOwners = initial?.owners?.length
    ? initial.owners
    : (initial?.ownerName ? [{ name: initial.ownerName || '', phone: initial.ownerPhone || '', email: initial.ownerEmail || '' }] : [{ name: '', phone: '', email: '' }]);

  const [form, setForm] = useState({
    name: '', addressLine1: '', addressLine2: '', pincode: '', city: '', state: '', country: '',
    owners: initOwners,
    gstRegistered: false, gstNumber: '',
    contractFileUrl: '', contractFileName: '', contractStart: '', contractRenewal: '',
    assignedUniversities: [],
    status: 'active', notes: '',
    ...initial,
    owners: initOwners,
    assignedUniversities: initial?.assignedUniversities || [],
    gstRegistered: !!initial?.gstRegistered,
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const sv = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Organization Name *">
        <input style={inputStyle} value={form.name} onChange={set('name')} placeholder="e.g. Global Study Abroad Pvt. Ltd." />
      </Field>

      <SectionHeading>Office Address</SectionHeading>
      <Field label="Address Line 1">
        <input style={inputStyle} value={form.addressLine1 || ''} onChange={set('addressLine1')} placeholder="Building / Street / Suite No." />
      </Field>
      <Field label="Address Line 2">
        <input style={inputStyle} value={form.addressLine2 || ''} onChange={set('addressLine2')} placeholder="Area / Locality (optional)" />
      </Field>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Field label="Pincode / ZIP" half><input style={inputStyle} value={form.pincode || ''} onChange={set('pincode')} placeholder="400001" /></Field>
        <Field label="City" half><input style={inputStyle} value={form.city || ''} onChange={set('city')} placeholder="City" /></Field>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Field label="State / Province" half><input style={inputStyle} value={form.state || ''} onChange={set('state')} placeholder="State" /></Field>
        <Field label="Country" half>
          <select style={inputStyle} value={form.country || ''} onChange={set('country')}>
            <option value="">Select Country</option>
            {COUNTRIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <SectionHeading>Owners / Directors</SectionHeading>
      <OwnersEditor owners={form.owners} onChange={v => sv('owners', v)} />

      <SectionHeading>GST Information</SectionHeading>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Field label="GST Registered?" half>
          <select style={inputStyle} value={form.gstRegistered ? 'yes' : 'no'} onChange={e => sv('gstRegistered', e.target.value === 'yes')}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </Field>
        {form.gstRegistered && (
          <Field label="GSTIN (15-digit)" half>
            <input style={inputStyle} value={form.gstNumber || ''} onChange={set('gstNumber')} placeholder="22AAAAA0000A1Z5" maxLength={15} />
          </Field>
        )}
      </div>

      <SectionHeading>Contract with Company</SectionHeading>
      <FileUploadField
        label="Contract Document"
        storedUrl={form.contractFileUrl}
        storedName={form.contractFileName}
        uploadPath={`companies/${companyId}/contracts/orgs`}
        onUploadComplete={({ url, name }) => setForm(f => ({ ...f, contractFileUrl: url, contractFileName: name }))}
      />
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
        <Field label="Contract Start Date" half><input style={inputStyle} type="date" value={form.contractStart || ''} onChange={set('contractStart')} /></Field>
        <Field label="Contract Renewal Date" half><input style={inputStyle} type="date" value={form.contractRenewal || ''} onChange={set('contractRenewal')} /></Field>
      </div>

      <SectionHeading>Assigned Universities & Commission Share</SectionHeading>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
        Select universities this organization can promote. Enter the % of the company's commission from each university that will go to this org.
      </div>
      <OrgUniAssignment assigned={form.assignedUniversities} universities={universities} onChange={v => sv('assignedUniversities', v)} />

      <SectionHeading>Status & Notes</SectionHeading>
      <Field label="Status" half>
        <select style={inputStyle} value={form.status || 'active'} onChange={set('status')}>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="blocked">Blocked</option>
        </select>
      </Field>
      <Field label="Notes">
        <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.notes || ''} onChange={set('notes')} placeholder="Any notes about this organization..." />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button style={btn('ghost')} onClick={onClose}>Cancel</button>
        <button style={btn('primary')} onClick={() => {
          if (!form.name.trim()) { alert('Organization name is required'); return; }
          onSave(form);
        }}>
          {initial?.id ? 'Save Changes' : 'Add Organization'}
        </button>
      </div>
    </div>
  );
}

function OrganizationsTab({ orgs, students, universities, companyId }) {
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [expandedId, setExpandedId]   = useState(null);
  const [showBulk, setShowBulk]       = useState(false);

  const filtered = useMemo(() => orgs.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    const q = search.toLowerCase();
    const primaryOwner = (o.owners?.[0]?.name || o.ownerName || '').toLowerCase();
    return !q || o.name?.toLowerCase().includes(q) || o.city?.toLowerCase().includes(q) || primaryOwner.includes(q);
  }), [orgs, search, statusFilter]);

  const appCounts = useMemo(() => {
    const m = {};
    students.forEach(s => { m[s.subAgentOrgId] = (m[s.subAgentOrgId] || 0) + 1; });
    return m;
  }, [students]);

  const handleSave = async (form) => {
    const data = { ...form, updatedAt: serverTimestamp() };
    if (editing?.id) {
      await updateDoc(doc(db, 'companies', companyId, 'subAgentOrgs', editing.id), data);
    } else {
      await addDoc(collection(db, 'companies', companyId, 'subAgentOrgs'), { ...data, createdAt: serverTimestamp() });
    }
    setShowModal(false); setEditing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this organization? Its applications will not be deleted.')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'subAgentOrgs', id));
  };

  const statusColors = {
    active:  { color: '#10b981', bg: '#ecfdf5' },
    pending: { color: '#f59e0b', bg: '#fffbeb' },
    blocked: { color: '#ef4444', bg: '#fef2f2' },
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, width: 220 }}
            placeholder="Search organizations..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <select style={{ ...inputStyle, width: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn('ghost')} onClick={() => setShowBulk(true)}>⬆ Bulk Import</button>
          <button style={btn('primary')} onClick={() => { setEditing(null); setShowModal(true); }}>+ Add Organization</button>
        </div>
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-subtle)' }}>
              {['', 'Organization', 'Location', 'Primary Owner', 'Universities', 'Apps', 'Status', ''].map((h, i) => (
                <th key={i} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '44px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                {orgs.length === 0 ? 'No organizations yet — add your first partner organization' : 'No organizations match your filters'}
              </td></tr>
            )}
            {filtered.map(o => {
              const sc   = statusColors[o.status] || statusColors.active;
              const apps = appCounts[o.id] || 0;
              const expanded = expandedId === o.id;
              const primaryOwner = o.owners?.[0] || (o.ownerName ? { name: o.ownerName, email: o.ownerEmail || '', phone: o.ownerPhone || '' } : null);
              const assignedCount = o.assignedUniversities?.length || 0;
              return (
                <React.Fragment key={o.id}>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: expanded ? 'var(--color-primary-soft)' : 'transparent' }}>
                    <td style={{ padding: '12px 10px', width: 36, cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : o.id)}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', userSelect: 'none' }}>{expanded ? '▼' : '▶'}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{o.name}</div>
                      {o.gstRegistered && <div style={{ fontSize: 10, color: '#059669', fontWeight: 600, marginTop: 2 }}>GST Registered{o.gstNumber ? `: ${o.gstNumber}` : ''}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontSize: 12 }}>
                      {[o.city, o.state, o.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {primaryOwner ? (
                        <>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{primaryOwner.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{primaryOwner.phone || primaryOwner.email || '—'}</div>
                          {(o.owners?.length || 0) > 1 && <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 2 }}>+{o.owners.length - 1} more</div>}
                        </>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: assignedCount ? '#eff6ff' : 'var(--bg-secondary)', color: assignedCount ? '#3b82f6' : 'var(--text-muted)' }}>
                        {assignedCount} {assignedCount === 1 ? 'university' : 'universities'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontWeight: 700, color: '#7c3aed', background: '#ede9fe', padding: '3px 10px', borderRadius: 20, fontSize: 12 }}>{apps}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: sc.color, background: sc.bg, textTransform: 'capitalize' }}>{o.status || 'active'}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={btn('ghost', 'sm')} onClick={() => { setEditing(o); setShowModal(true); }}>Edit</button>
                        <button style={btn('danger', 'sm')} onClick={() => handleDelete(o.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-secondary)' }}>
                      <td colSpan={8} style={{ padding: '18px 22px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 20 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Full Address</div>
                            <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.8 }}>
                              {[o.addressLine1, o.addressLine2, o.city, o.state, o.pincode, o.country].filter(Boolean).join(', ') || '—'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>All Owners</div>
                            {(o.owners || []).map((ow, i) => (
                              <div key={i} style={{ marginBottom: 6 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{ow.name}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{[ow.phone, ow.email].filter(Boolean).join(' · ') || '—'}</div>
                              </div>
                            ))}
                            {!o.owners?.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</div>}
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Contract</div>
                            {o.contractFileUrl
                              ? <a href={o.contractFileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--color-primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>📎 {o.contractFileName || 'View Contract'}</a>
                              : <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>No file uploaded</div>}
                            {o.contractStart && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Start: {o.contractStart}</div>}
                            {o.contractRenewal && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Renewal: {o.contractRenewal}</div>}
                          </div>
                          {assignedCount > 0 && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Assigned Universities & Commission Share</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {o.assignedUniversities.map(a => {
                                  const u = universities.find(u => u.id === a.uniId);
                                  return u ? (
                                    <div key={a.uniId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>{u.name}</span>
                                      {a.commissionSharePct && <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700 }}>→ {a.commissionSharePct}%</span>}
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit Organization' : 'Add Organization'} onClose={() => { setShowModal(false); setEditing(null); }} width={700}>
          <OrgForm initial={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} universities={universities} companyId={companyId} />
        </Modal>
      )}
      {showBulk && (
        <Modal title="Bulk Import Organizations" onClose={() => setShowBulk(false)} width={600}>
          <OrgBulkUpload companyId={companyId} onClose={() => setShowBulk(false)} />
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — UNIVERSITIES
// ═══════════════════════════════════════════════════════════════════════════════

function CommissionEntryEditor({ entry, onChange, onRemove, idx }) {
  const [programInput, setProgramInput] = useState('');
  const progs = entry.programs || [];

  const toggleProg = p => {
    onChange({ ...entry, programs: progs.includes(p) ? progs.filter(x => x !== p) : [...progs, p] });
  };

  const addPrograms = () => {
    if (!programInput.trim()) return;
    const newProgs = programInput.split(',').map(p => p.trim()).filter(Boolean);
    onChange({ ...entry, programs: [...new Set([...progs, ...newProgs])] });
    setProgramInput('');
  };

  return (
    <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Commission Entry #{idx + 1}</div>
        <button style={btn('danger', 'sm')} onClick={onRemove}>Remove</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 150 }}>
          <label style={labelStyle}>Degree Type *</label>
          <select style={inputStyle} value={entry.degreeType || ''} onChange={e => onChange({ ...entry, degreeType: e.target.value })}>
            <option value="">Select Degree</option>
            {DEGREE_TYPES.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 150 }}>
          <label style={labelStyle}>Commission Type *</label>
          <select style={inputStyle} value={entry.commissionType || ''} onChange={e => onChange({ ...entry, commissionType: e.target.value })}>
            <option value="">Select Type</option>
            {COMMISSION_TYPES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 150 }}>
          <label style={labelStyle}>Value ($ or %)</label>
          <input style={inputStyle} value={entry.value || ''} onChange={e => onChange({ ...entry, value: e.target.value })} placeholder="e.g. 1000 $ or 10%" />
        </div>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 150 }}>
          <label style={labelStyle}>Duration *</label>
          <select style={inputStyle} value={entry.duration || ''} onChange={e => onChange({ ...entry, duration: e.target.value })}>
            <option value="">Select Duration</option>
            {DURATION_TYPES.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 100%' }}>
          <label style={labelStyle}>Payment Cycle *</label>
          <input style={inputStyle} value={entry.paymentCycle || ''} onChange={e => onChange({ ...entry, paymentCycle: e.target.value })} placeholder="e.g. After 2nd semester" />
        </div>
        <div style={{ flex: '1 1 100%' }}>
          <label style={labelStyle}>Programs (type and press Enter or comma-separate multiple)</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={programInput}
              onChange={e => setProgramInput(e.target.value)}
              placeholder="e.g. Computer Science-BS, Biology-BS"
              onKeyDown={e => { if (e.key === 'Enter') { addPrograms(); e.preventDefault(); } }}
            />
            <button style={btn('ghost', 'sm')} onClick={addPrograms}>Add</button>
          </div>
          {progs.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {progs.map(p => (
                <span key={p} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
                  borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#eff6ff',
                  color: '#3b82f6', border: '1px solid #bfdbfe',
                }}>
                  {p}
                  <span style={{ cursor: 'pointer', fontSize: 14, lineHeight: 1 }} onClick={() => toggleProg(p)}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 200 }}>
          <label style={labelStyle}>T&C for University</label>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={entry.tcUniversity || ''} onChange={e => onChange({ ...entry, tcUniversity: e.target.value })} placeholder="Terms for university..." />
        </div>
        <div style={{ flex: '1 1 calc(50% - 6px)', minWidth: 200 }}>
          <label style={labelStyle}>T&C for Organization</label>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={entry.tcOrganization || ''} onChange={e => onChange({ ...entry, tcOrganization: e.target.value })} placeholder="Terms for sub-agent org..." />
        </div>
      </div>
    </div>
  );
}

// ── ProgramsEditor ────────────────────────────────────────────────────────────
function ProgramsEditor({ programs = [], onChange }) {
  const add = () => onChange([...programs, { name: '', stemType: 'STEM', degreeType: '', duration: '', currency: 'USD', tuitionPerYear: '', totalFee: '' }]);
  const upd = (i, k, v) => { const a = [...programs]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const rem = i => onChange(programs.filter((_, idx) => idx !== i));
  const stemColors = { 'STEM': '#059669', 'Non-STEM': '#3b82f6', 'Business': '#f59e0b', 'Law': '#7c3aed', 'Medicine': '#ef4444', 'Arts & Humanities': '#ec4899', 'Social Sciences': '#0891b2', 'Education': '#ea580c', 'Other': '#94a3b8' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{programs.length} program{programs.length !== 1 ? 's' : ''} added</span>
        <button style={btn('ghost', 'sm')} onClick={add}>+ Add Program</button>
      </div>
      {programs.length === 0 && <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No programs yet. Add programs or upload them later.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {programs.map((p, i) => {
          const sc = stemColors[p.stemType] || '#94a3b8';
          return (
            <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>Program #{i + 1}</span>
                  {p.stemType && <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, color: '#fff', background: sc }}>{p.stemType}</span>}
                </div>
                <button style={btn('danger', 'sm')} onClick={() => rem(i)}>Remove</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: '1 1 100%' }}>
                  <label style={labelStyle}>Program Name *</label>
                  <input style={inputStyle} value={p.name} onChange={e => upd(i, 'name', e.target.value)} placeholder="e.g. Computer Science - BS" />
                </div>
                <div style={{ flex: '1 1 calc(33% - 8px)', minWidth: 110 }}>
                  <label style={labelStyle}>STEM Type</label>
                  <select style={inputStyle} value={p.stemType || 'STEM'} onChange={e => upd(i, 'stemType', e.target.value)}>
                    {STEM_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 calc(33% - 8px)', minWidth: 110 }}>
                  <label style={labelStyle}>Degree Type</label>
                  <select style={inputStyle} value={p.degreeType || ''} onChange={e => upd(i, 'degreeType', e.target.value)}>
                    <option value="">Select</option>
                    {DEGREE_TYPES.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}>
                  <label style={labelStyle}>Duration</label>
                  <input style={inputStyle} value={p.duration || ''} onChange={e => upd(i, 'duration', e.target.value)} placeholder="e.g. 4 years" />
                </div>
                <div style={{ flex: '1 1 calc(25% - 8px)', minWidth: 80 }}>
                  <label style={labelStyle}>Currency</label>
                  <select style={inputStyle} value={p.currency || 'USD'} onChange={e => upd(i, 'currency', e.target.value)}>
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 calc(37% - 8px)', minWidth: 110 }}>
                  <label style={labelStyle}>Tuition / Year</label>
                  <input style={inputStyle} type="number" value={p.tuitionPerYear || ''} onChange={e => upd(i, 'tuitionPerYear', e.target.value)} placeholder="35000" />
                </div>
                <div style={{ flex: '1 1 calc(37% - 8px)', minWidth: 110 }}>
                  <label style={labelStyle}>Total Program Fee</label>
                  <input style={inputStyle} type="number" value={p.totalFee || ''} onChange={e => upd(i, 'totalFee', e.target.value)} placeholder="140000" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UniForm({ initial, onSave, onClose, companyId }) {
  const [form, setForm] = useState({
    name: '', country: '', contractType: 'Direct',
    contractFileUrl: '', contractFileName: '', contractStart: '', contractRenewal: '',
    pocName: '', pocEmail: '', pocPhone: '',
    promotedDegrees: [], programs: [], commissions: [],
    processDocUrl: '', notes: '',
    ...initial,
    promotedDegrees: initial?.promotedDegrees || [],
    programs: initial?.programs || [],
    commissions: initial?.commissions || [],
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const sv = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleDegree = d => sv('promotedDegrees', form.promotedDegrees.includes(d) ? form.promotedDegrees.filter(x => x !== d) : [...form.promotedDegrees, d]);
  const addCommission = () => sv('commissions', [...form.commissions, { degreeType: '', commissionType: '', value: '', duration: '', paymentCycle: '', programs: [], tcUniversity: '', tcOrganization: '' }]);
  const updateCommission = (i, data) => { const c = [...form.commissions]; c[i] = data; sv('commissions', c); };
  const removeCommission = i => sv('commissions', form.commissions.filter((_, idx) => idx !== i));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Field label="University Name *" half>
          <input style={inputStyle} value={form.name} onChange={set('name')} placeholder="e.g. Adelphi University" />
        </Field>
        <Field label="Contract Type" half>
          <select style={inputStyle} value={form.contractType} onChange={set('contractType')}>
            {CONTRACT_TYPES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Country" half>
        <select style={inputStyle} value={form.country} onChange={set('country')}>
          <option value="">Select Country</option>
          {UNI_COUNTRIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </Field>

      <SectionHeading>Contract with Company</SectionHeading>
      <FileUploadField
        label="Contract Document"
        storedUrl={form.contractFileUrl}
        storedName={form.contractFileName}
        uploadPath={`companies/${companyId}/contracts/universities`}
        onUploadComplete={({ url, name }) => setForm(f => ({ ...f, contractFileUrl: url, contractFileName: name }))}
      />
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
        <Field label="Contract Start Date" half><input style={inputStyle} type="date" value={form.contractStart || ''} onChange={set('contractStart')} /></Field>
        <Field label="Contract Renewal Date" half><input style={inputStyle} type="date" value={form.contractRenewal || ''} onChange={set('contractRenewal')} /></Field>
      </div>

      <SectionHeading>Point of Contact (POC)</SectionHeading>
      <Field label="POC Name">
        <input style={inputStyle} value={form.pocName} onChange={set('pocName')} placeholder="University representative name" />
      </Field>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Field label="POC Email" half><input style={inputStyle} type="email" value={form.pocEmail} onChange={set('pocEmail')} placeholder="poc@university.edu" /></Field>
        <Field label="POC Phone" half><input style={inputStyle} value={form.pocPhone} onChange={set('pocPhone')} placeholder="+1 XXX XXX XXXX" /></Field>
      </div>

      <SectionHeading>Promoted Degrees</SectionHeading>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {DEGREE_TYPES.map(d => (
          <span key={d} onClick={() => toggleDegree(d)} style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: form.promotedDegrees.includes(d) ? 'var(--color-primary)' : 'var(--bg-secondary)', color: form.promotedDegrees.includes(d) ? '#fff' : 'var(--text-secondary)', border: `1px solid ${form.promotedDegrees.includes(d) ? 'var(--color-primary)' : 'var(--border-subtle)'}`, transition: 'all 0.15s' }}>{d}</span>
        ))}
      </div>

      <SectionHeading>Programs & Fee Structure</SectionHeading>
      <ProgramsEditor programs={form.programs} onChange={v => sv('programs', v)} />

      <SectionHeading>Commission Entries</SectionHeading>
      {form.commissions.map((c, i) => (
        <CommissionEntryEditor key={i} idx={i} entry={c} onChange={data => updateCommission(i, data)} onRemove={() => removeCommission(i)} />
      ))}
      {form.commissions.length === 0 && <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: 12 }}>No commission entries yet</div>}
      <button style={{ ...btn('ghost', 'sm'), alignSelf: 'flex-start' }} onClick={addCommission}>+ Add Commission Entry</button>

      <SectionHeading>Additional</SectionHeading>
      <Field label="Process Document URL">
        <input style={inputStyle} value={form.processDocUrl} onChange={set('processDocUrl')} placeholder="https://drive.google.com/..." />
      </Field>
      <Field label="Notes">
        <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Notes about this university partnership..." />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
        <button style={btn('ghost')} onClick={onClose}>Cancel</button>
        <button style={btn('primary')} onClick={() => {
          if (!form.name.trim()) { alert('University name is required'); return; }
          onSave(form);
        }}>
          {initial?.id ? 'Save Changes' : 'Add University'}
        </button>
      </div>
    </div>
  );
}

function UniversitiesTab({ universities, companyId }) {
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showBulk, setShowBulk]     = useState(false);

  const filtered = useMemo(() => universities.filter(u => {
    const q = search.toLowerCase();
    return !q || u.name?.toLowerCase().includes(q) || u.country?.toLowerCase().includes(q);
  }), [universities, search]);

  const handleSave = async (form) => {
    const data = { ...form, updatedAt: serverTimestamp() };
    if (editing?.id) {
      await updateDoc(doc(db, 'companies', companyId, 'subAgentUniversities', editing.id), data);
    } else {
      await addDoc(collection(db, 'companies', companyId, 'subAgentUniversities'), { ...data, createdAt: serverTimestamp() });
    }
    setShowModal(false); setEditing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this university?')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'subAgentUniversities', id));
  };

  const contractColors = {
    Direct:     { color: '#10b981', bg: '#ecfdf5' },
    Shorelight: { color: '#3b82f6', bg: '#eff6ff' },
    Other:      { color: '#94a3b8', bg: '#f1f5f9' },
  };

  const typeColors = {
    Fixed:       { color: '#10b981', bg: '#ecfdf5' },
    Percentage:  { color: '#3b82f6', bg: '#eff6ff' },
    Custom:      { color: '#f97316', bg: '#fff7ed' },
    Scholarship: { color: '#8b5cf6', bg: '#f5f3ff' },
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <input style={{ ...inputStyle, width: 250 }} placeholder="Search universities..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn('ghost')} onClick={() => setShowBulk(true)}>⬆ Bulk Import</button>
          <button style={btn('primary')} onClick={() => { setEditing(null); setShowModal(true); }}>+ Add University</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.length === 0 && (
          <div style={{ ...card, padding: '44px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {universities.length === 0 ? 'No universities yet — add your first partner university' : 'No universities match your search'}
          </div>
        )}
        {filtered.map(u => {
          const expanded = expandedId === u.id;
          const cc = contractColors[u.contractType] || contractColors.Other;
          return (
            <div key={u.id} style={card}>
              {/* Header row */}
              <div
                style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedId(expanded ? null : u.id)}
              >
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10, background: 'var(--color-primary-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 17, fontWeight: 800, color: 'var(--color-primary)', flexShrink: 0,
                  }}>{(u.name || 'U')[0].toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {[u.country, u.pocName && `POC: ${u.pocName}`, u.programs?.length && `${u.programs.length} program${u.programs.length === 1 ? '' : 's'}`, u.commissions?.length && `${u.commissions.length} commission entr${u.commissions.length === 1 ? 'y' : 'ies'}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {(u.promotedDegrees || []).map(d => (
                      <span key={d} style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: '#ede9fe', color: '#7c3aed' }}>{d}</span>
                    ))}
                    {u.contractType && (
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: cc.color, background: cc.bg }}>{u.contractType}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button style={btn('ghost')} onClick={() => { setEditing(u); setShowModal(true); }}>✏️</button>
                  <button style={btn('ghost')} onClick={() => handleDelete(u.id)}>🗑️</button>
                  <span style={{ fontSize: 18, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', marginLeft: 4 }}>{expanded ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Expanded details */}
              {expanded && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 16 }}>

                    {/* Contract info */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Contract Details</div>
                      {u.contractFileUrl
                        ? <a href={u.contractFileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--color-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>📎 {u.contractFileName || 'View Contract'}</a>
                        : <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>No contract uploaded</div>
                      }
                      {(u.contractStart || u.contractRenewal) && (
                        <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                          {u.contractStart   && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Start: <strong>{u.contractStart}</strong></div>}
                          {u.contractRenewal && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Renewal: <strong>{u.contractRenewal}</strong></div>}
                        </div>
                      )}
                      {u.pocEmail && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>POC: {u.pocName} · {u.pocEmail} {u.pocPhone && `· ${u.pocPhone}`}</div>}
                      {u.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, fontStyle: 'italic' }}>{u.notes}</div>}
                    </div>

                    {/* Programs */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Programs ({(u.programs || []).length})</div>
                      {(u.programs || []).length === 0
                        ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No programs added yet</div>
                        : (u.programs || []).map((p, pi) => {
                          const sc = { 'STEM': '#059669', 'Non-STEM': '#3b82f6', 'Business': '#f59e0b', 'Law': '#7c3aed', 'Medicine': '#ef4444', 'Arts & Humanities': '#ec4899', 'Social Sciences': '#0891b2', 'Education': '#ea580c', 'Other': '#94a3b8' }[p.stemType] || '#94a3b8';
                          return (
                            <div key={pi} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, fontSize: 12 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                              <span style={{ color: 'var(--text-primary)', fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                              <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{p.stemType}</span>
                              {(p.tuitionPerYear || p.totalFee) && (
                                <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                                  {p.currency || 'USD'} {p.tuitionPerYear ? `${Number(p.tuitionPerYear).toLocaleString()}/yr` : `${Number(p.totalFee).toLocaleString()} total`}
                                </span>
                              )}
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>

                  {/* Commissions */}
                  {(u.commissions || []).length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Commission Structure</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {(u.commissions || []).map((c, ci) => {
                          const tc = typeColors[c.type] || typeColors.Custom;
                          return (
                            <span key={ci} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: tc.color, background: tc.bg }}>
                              {c.type} · {c.level} · {c.value}{c.type === 'Percentage' ? '%' : ` ${c.currency || 'USD'}`}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* UniForm Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{editing ? 'Edit University' : 'Add University'}</h2>
              <button style={btn('ghost')} onClick={() => { setShowModal(false); setEditing(null); }}>✕</button>
            </div>
            <UniForm initial={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} companyId={companyId} />
          </div>
        </div>
      )}

      {/* UniBulkUpload Modal */}
      {showBulk && <UniBulkUpload companyId={companyId} onClose={() => setShowBulk(false)} />}
    </div>
  );
}

// ─── APPLICATIONS TAB ────────────────────────────────────────────────────────────
function StudentForm({ initial, onSave, onClose, orgs, universities, companyUsers }) {
  const blank = { studentName:'', email:'', phone:'', country:'India', orgId:'', universityId:'', program:'', intake:'', stage:'draft', assignedTo:'', notes:'' };
  const [form, setForm] = useState(() => initial ? { ...blank, ...initial } : blank);
  const sv = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const set = k => e => sv(k, e.target.value);

  const selUni = universities.find(u => u.id === form.universityId);
  const uniPrograms = selUni?.programs || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Student Name *</label>
          <input style={inputStyle} value={form.studentName} onChange={set('studentName')} placeholder="Full name" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Email</label>
          <input style={inputStyle} type="email" value={form.email} onChange={set('email')} placeholder="student@email.com" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Phone</label>
          <input style={inputStyle} value={form.phone} onChange={set('phone')} placeholder="+91 XXXXX XXXXX" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Home Country</label>
          <select style={inputStyle} value={form.country} onChange={set('country')}>
            {COUNTRIES.map(c => <option key={c}>{c}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Partner Organization</label>
          <select style={inputStyle} value={form.orgId} onChange={set('orgId')}>
            <option value="">— Select Org —</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Target University</label>
          <select style={inputStyle} value={form.universityId} onChange={e => { sv('universityId', e.target.value); sv('program', ''); }}>
            <option value="">— Select University —</option>
            {universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Program</label>
          {uniPrograms.length > 0
            ? <select style={inputStyle} value={form.program} onChange={set('program')}>
                <option value="">— Select Program —</option>
                {uniPrograms.map((p, i) => <option key={i} value={p.name}>{p.name}</option>)}
              </select>
            : <input style={inputStyle} value={form.program} onChange={set('program')} placeholder="e.g. MSc Computer Science" />
          }</div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Intake</label>
          <select style={inputStyle} value={form.intake} onChange={set('intake')}>
            <option value="">— Select Intake —</option>
            {INTAKE_OPTIONS.map(i => <option key={i}>{i}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Stage</label>
          <select style={inputStyle} value={form.stage} onChange={set('stage')}>
            {APP_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Assigned Counselor</label>
          <select style={inputStyle} value={form.assignedTo} onChange={set('assignedTo')}>
            <option value="">— Unassigned —</option>
            {companyUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select></div>
      </div>
      <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Notes</label>
        <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Any additional notes..." /></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
        <button style={btn('ghost')} onClick={onClose}>Cancel</button>
        <button style={btn('primary')} onClick={() => { if (!form.studentName.trim()) { alert('Student name required'); return; } onSave(form); }}>
          {initial?.id ? 'Save Changes' : 'Add Application'}
        </button>
      </div>
    </div>
  );
}

function ApplicationsTab({ students, orgs, universities, companyUsers, companyId }) {
  const [search, setSearch]           = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterOrg, setFilterOrg]     = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return students.filter(s => {
      if (filterStage && (s.stage || 'draft') !== filterStage) return false;
      if (filterOrg  && s.orgId !== filterOrg) return false;
      if (!q) return true;
      const org = orgs.find(o => o.id === s.orgId);
      const uni = universities.find(u => u.id === s.universityId);
      return s.studentName?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) ||
             org?.name?.toLowerCase().includes(q) || uni?.name?.toLowerCase().includes(q) ||
             s.program?.toLowerCase().includes(q);
    });
  }, [students, orgs, universities, search, filterStage, filterOrg]);

  const handleSave = async (form) => {
    const data = { ...form, updatedAt: serverTimestamp() };
    if (editing?.id) {
      await updateDoc(doc(db, 'companies', companyId, 'subAgentStudents', editing.id), data);
    } else {
      await addDoc(collection(db, 'companies', companyId, 'subAgentStudents'), { ...data, createdAt: serverTimestamp() });
    }
    setShowModal(false); setEditing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'subAgentStudents', id));
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inputStyle, width: 220 }} placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...inputStyle, width: 160 }} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
            <option value="">All Stages</option>
            {APP_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select style={{ ...inputStyle, width: 180 }} value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
            <option value="">All Organizations</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <button style={btn('primary')} onClick={() => { setEditing(null); setShowModal(true); }}>+ Add Application</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Showing {filtered.length} of {students.length} applications
      </div>
      {filtered.length === 0
        ? <div style={{ ...card, padding: '44px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {students.length === 0 ? 'No applications yet — add your first student application' : 'No applications match your filters'}
          </div>
        : <div style={{ ...card, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-subtle)' }}>
                  {['Student', 'Country', 'Organization', 'University', 'Program', 'Intake', 'Stage', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const org = orgs.find(o => o.id === s.orgId);
                  const uni = universities.find(u => u.id === s.universityId);
                  const st  = APP_STAGES.find(x => x.id === (s.stage || 'draft'));
                  const counselor = companyUsers.find(u => u.id === s.assignedTo);
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.studentName}</div>
                        {s.email && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.email}</div>}
                        {counselor && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>👤 {counselor.name}</div>}
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{s.country || '—'}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org?.name || '—'}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uni?.name || '—'}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.program || '—'}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{s.intake || '—'}</td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        {st && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: st.color, background: st.bg }}>{st.label}</span>}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={btn('ghost')} onClick={() => { setEditing(s); setShowModal(true); }}>✏️</button>
                          <button style={btn('ghost')} onClick={() => handleDelete(s.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      }
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{editing ? 'Edit Application' : 'Add Application'}</h2>
              <button style={btn('ghost')} onClick={() => { setShowModal(false); setEditing(null); }}>✕</button>
            </div>
            <StudentForm initial={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} orgs={orgs} universities={universities} companyUsers={companyUsers} />
          </div>
        </div>
      )}
    </div>
  );
}

function CommissionTab({ universities }) {
  const typeColors = {
    Fixed:       { color: '#10b981', bg: '#ecfdf5' },
    Percentage:  { color: '#3b82f6', bg: '#eff6ff' },
    Custom:      { color: '#f97316', bg: '#fff7ed' },
    Scholarship: { color: '#8b5cf6', bg: '#f5f3ff' },
  };
  const unisWithCommissions = universities.filter(u => (u.commissions || []).length > 0);
  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, padding: '18px 22px', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>Commission Overview</div>
        <div style={{ display: 'flex', gap: 32 }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 900, color: '#fff' }}>{universities.length}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Partner Universities</div>
          </div>
          <div>
            <div style={{ fontSize: 32, fontWeight: 900, color: '#a78bfa' }}>{unisWithCommissions.length}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>With Commission Structure</div>
          </div>
          <div>
            <div style={{ fontSize: 32, fontWeight: 900, color: '#34d399' }}>
              {universities.reduce((acc, u) => acc + (u.commissions || []).length, 0)}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Total Commission Entries</div>
          </div>
        </div>
      </div>
      {universities.length === 0 && (
        <div style={{ ...card, padding: '44px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No universities added yet. Add universities with commission structures to track earnings here.
        </div>
      )}
      {universities.map(u => {
        const commissions = u.commissions || [];
        if (commissions.length === 0) return null;
        return (
          <div key={u.id} style={card}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{u.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{u.country}{u.contractType ? ` · ${u.contractType}` : ''}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed' }}>
                {commissions.length} entr{commissions.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
            <div style={{ padding: '14px 20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Degree Level', 'Type', 'Value', 'Duration', 'Notes'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((c, ci) => {
                    const tc = typeColors[c.type] || typeColors.Custom;
                    return (
                      <tr key={ci} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '10px', fontWeight: 600, color: 'var(--text-primary)' }}>{c.level || '—'}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: tc.color, background: tc.bg }}>{c.type}</span>
                        </td>
                        <td style={{ padding: '10px', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {c.type === 'Percentage' ? `${c.value}%` : `${c.currency || 'USD'} ${Number(c.value || 0).toLocaleString()}`}
                        </td>
                        <td style={{ padding: '10px', color: 'var(--text-secondary)' }}>{c.durationType || '—'}</td>
                        <td style={{ padding: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {universities.length > 0 && unisWithCommissions.length === 0 && (
        <div style={{ ...card, padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Universities are added but none have commission structures yet. Edit a university to add commission entries.
        </div>
      )}
    </div>
  );
}

export default function SubAgents({ company }) {
  const companyId = company?.id;
  const [activeTab, setActiveTab]       = useState('Overview');
  const [orgs, setOrgs]                 = useState([]);
  const [universities, setUniversities] = useState([]);
  const [students, setStudents]         = useState([]);
  const [safeUsers, setSafeUsers]       = useState([]);

  useEffect(() => {
    if (!companyId) return;
    const u1 = onSnapshot(collection(db, 'companies', companyId, 'subAgentOrgs'),        s => setOrgs(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, 'companies', companyId, 'subAgentUniversities'), s => setUniversities(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u3 = onSnapshot(collection(db, 'companies', companyId, 'subAgentStudents'),    s => setStudents(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u4 = onSnapshot(collection(db, 'companies', companyId, 'users'),               s => setSafeUsers(s.docs.map(d => ({ id: d.id, name: d.data().name, email: d.data().email }))));
    return () => { u1(); u2(); u3(); u4(); };
  }, [companyId]);

  const em = company?.enabledModules || [];
  if (!em.includes('subAgents')) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Sub-Agent module not enabled</div>
        <div style={{ fontSize: 13 }}>Ask your super admin to enable the Sub-Agents module for your company.</div>
      </div>
    );
  }

  const tabs = ['Overview', 'Organizations', 'Universities', 'Applications', 'Commission'];
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-secondary)', overflow: 'hidden' }}>
      <div style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-subtle)', padding: '0 28px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} style={{
              padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: activeTab === t ? 'var(--color-primary)' : 'var(--text-secondary)',
              borderBottom: activeTab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}>{t}</button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {activeTab === 'Overview'      && <OverviewTab orgs={orgs} students={students} universities={universities} />}
        {activeTab === 'Organizations' && <OrganizationsTab orgs={orgs} students={students} universities={universities} companyId={companyId} />}
        {activeTab === 'Universities'  && <UniversitiesTab universities={universities} companyId={companyId} />}
        {activeTab === 'Applications'  && <ApplicationsTab students={students} orgs={orgs} universities={universities} companyUsers={safeUsers} companyId={companyId} />}
        {activeTab === 'Commission'    && <CommissionTab universities={universities} />}
      </div>
    </div>
  );
}
